/**
 * YourSuggestions (PRP-MCP-12 work item 1, F4): on /assistant, the suggestions
 * the signed-in user sent from chat, with reference, date, status and any reply.
 *
 * Reads `/mcp/suggestions`, which returns only the caller's own rows. When the
 * server has MCP off (404) or the list cannot be read, the section renders
 * nothing: it is a convenience, never a blocker on the connect page.
 */
import { useEffect, useState } from 'react';
import type { ComponentType } from 'react';

import { SuggestionsAPI } from '../../lib/suggestionsApi';
import type { MySuggestion } from '../../lib/suggestionsApi';
import type { McpSectionFrameProps } from './McpConnectSection';

const fmtDate = (iso: string) => (iso ? new Date(iso).toLocaleDateString() : '');

export default function YourSuggestions({ frame: Frame }: { frame: ComponentType<McpSectionFrameProps> }) {
  const [items, setItems] = useState<MySuggestion[] | null>(null);

  useEffect(() => {
    let live = true;
    SuggestionsAPI.mine()
      .then((res) => live && setItems(res.items ?? []))
      .catch(() => live && setItems(null));
    return () => {
      live = false;
    };
  }, []);

  if (items === null) return null;
  return (
    <Frame title="Your suggestions" sub="What you sent to Antero Trail from chat. Rich reads every one.">
      {items.length === 0 ? (
        <p className="m-0 text-[13px] text-[var(--tx-lo)]" data-testid="suggestions-empty">
          None yet. In chat, tell the assistant what it should have done and ask it to send a suggestion.
        </p>
      ) : (
        <ul className="m-0 list-none space-y-3 p-0" data-testid="suggestions-list">
          {items.map((s) => (
            <li key={s.ref} className="border-t border-[var(--bd-subtle)] pt-3 first:border-t-0 first:pt-0">
              <div className="flex flex-wrap items-baseline gap-x-3 text-[13px]">
                <span className="font-mono text-[var(--tx-hi)]">{s.ref}</span>
                <span className="text-[var(--tx-faint)]">{fmtDate(s.created_at)}</span>
                <span className="text-[var(--tx-lo)]" data-testid="suggestion-status">
                  {s.status_label || s.status}
                </span>
              </div>
              {s.what_you_asked && <p className="mb-0 mt-1 text-[13px] text-[var(--tx-lo)]">{s.what_you_asked}</p>}
              {s.replies.map((r, i) => (
                <p key={i} className="mb-0 mt-1 whitespace-pre-wrap text-[13px] text-[var(--tx-hi)]">
                  <span className="text-[var(--tx-faint)]">Reply: </span>
                  {r.text}
                </p>
              ))}
            </li>
          ))}
        </ul>
      )}
    </Frame>
  );
}
